import { useEffect } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import { Textarea } from '@/app/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { CatalogSelect } from '@/app/components/ui/CatalogSelect'
import { DeliveryDetails, posSalesService, posSettingsService } from '@/app/services/pos.service'

const deliverySchema = z.object({
  deliveryUserId: z.number().min(1, 'Seleccione un repartidor'),
  recipientName: z.string().min(3, 'El nombre debe tener al menos 3 caracteres'),
  customerPhone: z.string().min(10, 'El teléfono debe tener al menos 10 caracteres'),
  customerAddress: z.string().min(10, 'La dirección debe tener al menos 10 caracteres'),
  deliveryCost: z.number().min(0, 'El costo no puede ser negativo'),
  observations: z.string().optional(),
})

type DeliveryFormValues = z.infer<typeof deliverySchema>

export interface DeliveryDetailsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  deliveryDetails: DeliveryDetails
  setDeliveryDetails: (details: DeliveryDetails) => void
  setShowDeliveryDetailsModal: (show: boolean) => void
}

export function DeliveryDetailsModal({
  open,
  onOpenChange,
  deliveryDetails,
  setDeliveryDetails,
  setShowDeliveryDetailsModal,
}: DeliveryDetailsModalProps) {

  const { data: defaultDeliveryFee, isLoading: isLoadingFee } = useQuery({
    queryKey: ['settings', 'sales.delivery_fee_amount'],
    queryFn: () => posSettingsService.getSetting('sales.delivery_fee_amount'),
  })

  const form = useForm<DeliveryFormValues>({
    resolver: zodResolver(deliverySchema),
    mode: 'onChange',
    defaultValues: {
      deliveryUserId: deliveryDetails.deliveryUserId || 0,
      recipientName: deliveryDetails.recipientName || '',
      customerPhone: deliveryDetails.customerPhone || '',
      customerAddress: deliveryDetails.customerAddress || '',
      deliveryCost: deliveryDetails.deliveryCost || 0,
      observations: deliveryDetails.observations || '',
    },
  })

  // Set default values when modal opens or fee/details change
  useEffect(() => {
    if (open) {
      let fee = 0
      if (deliveryDetails.deliveryCost > 0) {
        fee = deliveryDetails.deliveryCost
      } else if (defaultDeliveryFee !== undefined) {
        fee = parseFloat(typeof defaultDeliveryFee === 'object' ? defaultDeliveryFee?.value : defaultDeliveryFee) || 0
      }

      form.reset({
        deliveryUserId: deliveryDetails.deliveryUserId || 0,
        recipientName: deliveryDetails.recipientName || '',
        customerPhone: deliveryDetails.customerPhone || '',
        customerAddress: deliveryDetails.customerAddress || '',
        deliveryCost: fee,
        observations: deliveryDetails.observations || '',
      })
    }
  }, [open, deliveryDetails, defaultDeliveryFee, form])

  const onSubmit = (values: DeliveryFormValues) => {
    setDeliveryDetails({
      deliveryUserId: values.deliveryUserId,
      recipientName: values.recipientName,
      customerPhone: values.customerPhone,
      customerAddress: values.customerAddress,
      deliveryCost: values.deliveryCost,
      observations: values.observations || '',
    })
    setShowDeliveryDetailsModal(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Datos de Entrega a Domicilio</DialogTitle>
          <DialogDescription className="sr-only">
            Formulario para capturar detalles de la entrega a domicilio
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Repartidor <span className="text-red-600">*</span>
            </label>
            <Controller
              name="deliveryUserId"
              control={form.control}
              render={({ field }) => (
                <CatalogSelect
                  catalog="pos/delivery-users"
                  value={field.value && field.value > 0 ? field.value : ''}
                  onChange={(val) => field.onChange(val ? Number(val) : 0)}
                  placeholder="Seleccione un repartidor"
                  className={form.formState.errors.deliveryUserId ? "border-red-500" : ""}
                  itemLabelFormatter={(item: any) => `${item.name} ${item.last_name || ''}`.trim()}
                />
              )}
            />
            {form.formState.errors.deliveryUserId && (
              <p className="text-xs text-red-500">{form.formState.errors.deliveryUserId.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Nombre del Cliente/Destinatario <span className="text-red-600">*</span>
            </label>
            <Input
              {...form.register('recipientName')}
              placeholder="Nombre de quien recibe"
              className={form.formState.errors.recipientName ? "border-red-500" : ""}
            />
            {form.formState.errors.recipientName && (
              <p className="text-xs text-red-500">{form.formState.errors.recipientName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Teléfono del Cliente <span className="text-red-600">*</span>
            </label>
            <Input
              {...form.register('customerPhone')}
              placeholder="555-1234"
              className={form.formState.errors.customerPhone ? "border-red-500" : ""}
            />
            {form.formState.errors.customerPhone && (
              <p className="text-xs text-red-500">{form.formState.errors.customerPhone.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Domicilio de Entrega <span className="text-red-600">*</span>
            </label>
            <Textarea
              {...form.register('customerAddress')}
              placeholder="Calle, número, colonia, código postal..."
              rows={3}
              className={form.formState.errors.customerAddress ? "border-red-500" : ""}
            />
            {form.formState.errors.customerAddress && (
              <p className="text-xs text-red-500">{form.formState.errors.customerAddress.message}</p>
            )}
          </div>



          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Observaciones
            </label>
            <Textarea
              {...form.register('observations')}
              placeholder="Notas adicionales sobre la entrega..."
              rows={2}
            />
          </div>

          <div className="flex gap-2 border-t border-slate-200 pt-4">
            <Button
              type="submit"
              className="flex-1"
            >
              <Check className="mr-2 h-4 w-4" />
              Guardar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeliveryDetailsModal(false)}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
